import {OS} from '@github-ui/get-os'
import type {GenericAsset} from '../../../../../brand/lib/types/contentful'

// Matches Primer Brand's `isLarge` breakpoint.
export const DESKTOP_MEDIA_QUERY = '(min-width: 1012px)'

export type SlideMedia = {
  /** Shown by default, and on small screens. */
  fallback: GenericAsset
  /** Shown instead of `fallback` when `DESKTOP_MEDIA_QUERY` matches. */
  desktop?: GenericAsset
  /** Images shown while a video loads, picked with the same rules as the video. */
  poster?: {fallback: GenericAsset; desktop?: GenericAsset}
}

const hasFile = (item: GenericAsset) => Boolean(item.fields.asset.fields.file)

const contentTypeOf = (item: GenericAsset) => item.fields.asset.fields.file?.contentType ?? ''
export const isVideo = (item: GenericAsset) => contentTypeOf(item).startsWith('video/')
export const isImage = (item: GenericAsset) => contentTypeOf(item).startsWith('image/')

/**
 * Picks the slide's assets by their Contentful `id`:
 *
 * - `macosMobile`    -> iOS, and macOS by default
 * - `macosDesktop`   -> macOS on large screens
 * - `windowsDesktop` -> any other OS on large screens
 * - `windowsMobile`  -> default
 *
 * An id can be used twice: once by a video and once by an image. The image is then the video's
 * poster, shown while the video loads. A video needs a poster for each of its sizes, otherwise
 * the slide has no poster.
 *
 * The screen size is left to the browser (media queries). Only the OS needs JS.
 * Slides without a `windowsMobile` entry use their first asset (preferring a video), paired the same way.
 */
export function pickSlideMedia(media: readonly GenericAsset[] | undefined, os: OS): SlideMedia | undefined {
  const byId = (id: string) => {
    const matches = media?.filter(item => item.fields.id === id && hasFile(item))
    return matches?.find(isVideo) ?? matches?.[0]
  }
  const posterOf = (video: GenericAsset) =>
    isVideo(video) && video.fields.id
      ? media?.find(item => item.fields.id === video.fields.id && isImage(item) && hasFile(item))
      : undefined
  const withPoster = (slide: Omit<SlideMedia, 'poster'>): SlideMedia => {
    const fallback = posterOf(slide.fallback)
    const desktop = slide.desktop ? posterOf(slide.desktop) : undefined
    if (!fallback || (slide.desktop && !desktop)) return slide
    return {...slide, poster: {fallback, desktop}}
  }

  const windowsMobile = byId('windowsMobile')

  if (!windowsMobile) {
    const first = media?.find(item => hasFile(item) && isVideo(item)) ?? media?.find(hasFile)
    return first ? withPoster({fallback: first}) : undefined
  }

  if (os === OS.iOS) return withPoster({fallback: byId('macosMobile') ?? windowsMobile})

  if (os === OS.macOS) {
    return withPoster({
      fallback: byId('macosMobile') ?? windowsMobile,
      desktop: byId('macosDesktop') ?? byId('windowsDesktop'),
    })
  }

  return withPoster({fallback: windowsMobile, desktop: byId('windowsDesktop')})
}
